/**
 * AOS - Animate On Scroll Library
 * Custom implementation for Shopify themes
 * Optimized for performance and Shopify's requirements
 */

class AOS {
  constructor() {
    this.options = {
      offset: 120,
      delay: 0,
      duration: 400,
      easing: 'ease',
      once: false,
      mirror: false,
      anchorPlacement: 'top-bottom',
      disable: false,
      startEvent: 'DOMContentLoaded',
      initClassName: 'aos-init',
      animatedClassName: 'aos-animate',
      useClassOnHtml: false,
      disableMutationObserver: false,
      debounceDelay: 50,
      throttleDelay: 99
    };

    this.initialized = false;
    this.elements = [];
    this.listener = this.scrollHandler.bind(this);
    this.resizeListener = this.refreshHard.bind(this);

    // Throttle scroll handler
    this.throttledScrollHandler = this.throttle(this.listener, this.options.throttleDelay);
  }

  /**
   * Initialize AOS
   */
  init(options = {}) {
    this.options = { ...this.options, ...options };

    // Don't initialize if disabled
    if (this.shouldDisable()) {
      return false;
    }

    // Add body class if requested
    if (this.options.useClassOnHtml) {
      document.documentElement.classList.add(this.options.initClassName);
    } else {
      document.body.classList.add(this.options.initClassName);
    }

    // Prepare elements
    this.refresh();

    // Bind events
    this.bindEvents();

    this.initialized = true;
    return this;
  }

  /**
   * Refresh AOS elements
   */
  refresh() {
    this.elements = this.getElements();

    if (this.initialized) {
      this.refreshHard();
    }

    return this;
  }

  /**
   * Hard refresh - recalculates all positions
   */
  refreshHard() {
    this.elements.forEach(element => {
      element.position = this.getPositionIn(element.node);
      element.options = this.getElementOptions(element.node);
    });

    this.scrollHandler();
    return this;
  }

  /**
   * Get all AOS elements
   */
  getElements() {
    const elements = document.querySelectorAll('[data-aos]');
    return Array.from(elements).map(node => ({
      node,
      position: this.getPositionIn(node),
      options: this.getElementOptions(node)
    }));
  }

  /**
   * Get element options from data attributes
   */
  getElementOptions(element) {
    const elementOptions = {};
    const dataAttributes = element.dataset;

    // Map data attributes to options
    Object.keys(dataAttributes).forEach(key => {
      if (key.startsWith('aos')) {
        const optionKey = key.replace(/^aos([A-Z])/, (match, letter) =>
          letter.toLowerCase()
        ).replace(/^aos/, '');

        let value = dataAttributes[key];

        // Parse numeric values
        if (!isNaN(value) && value !== '') {
          value = parseInt(value);
        }

        // Parse boolean values
        if (value === 'true') value = true;
        if (value === 'false') value = false;

        if (optionKey) {
          elementOptions[optionKey] = value;
        }
      }
    });

    return { ...this.options, ...elementOptions };
  }

  /**
   * Calculate element position
   */
  getPositionIn(element) {
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft,
      bottom: rect.bottom + scrollTop,
      right: rect.right + scrollLeft,
      width: rect.width,
      height: rect.height
    };
  }

  /**
   * Check if element should be animated
   */
  shouldAnimate(element, scrollTop) {
    const { top, bottom } = element.position;
    const { offset, anchorPlacement, once, mirror } = element.options;
    const windowHeight = window.innerHeight;

    let triggerPoint;

    // Calculate trigger point based on anchor placement
    switch (anchorPlacement) {
      case 'top-bottom':
        triggerPoint = top + offset;
        break;
      case 'top-center':
        triggerPoint = top + offset - windowHeight / 2;
        break;
      case 'top-top':
        triggerPoint = top + offset - windowHeight;
        break;
      case 'center-bottom':
        triggerPoint = top + element.position.height / 2 + offset;
        break;
      case 'center-center':
        triggerPoint = top + element.position.height / 2 + offset - windowHeight / 2;
        break;
      case 'center-top':
        triggerPoint = top + element.position.height / 2 + offset - windowHeight;
        break;
      case 'bottom-bottom':
        triggerPoint = bottom + offset;
        break;
      case 'bottom-center':
        triggerPoint = bottom + offset - windowHeight / 2;
        break;
      case 'bottom-top':
        triggerPoint = bottom + offset - windowHeight;
        break;
      default:
        triggerPoint = top + offset;
    }

    const isVisible = scrollTop + windowHeight >= triggerPoint && scrollTop <= bottom;

    // Handle once and mirror options
    if (once && element.animated) {
      return false;
    }

    if (!mirror && scrollTop > bottom) {
      return false;
    }

    return isVisible;
  }

  /**
   * Animate element
   */
  animateElement(element) {
    const { delay, duration } = element.options;

    // Add delay if specified
    if (delay) {
      setTimeout(() => {
        this.addAnimationClass(element);
      }, delay);
    } else {
      this.addAnimationClass(element);
    }

    element.animated = true;
  }

  /**
   * Remove animation from element
   */
  removeAnimation(element) {
    element.node.classList.remove(this.options.animatedClassName);
    element.animated = false;
  }

  /**
   * Add animation class to element
   */
  addAnimationClass(element) {
    element.node.classList.add(this.options.animatedClassName);
  }

  /**
   * Main scroll handler
   */
  scrollHandler() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    this.elements.forEach(element => {
      if (this.shouldAnimate(element, scrollTop)) {
        if (!element.animated) {
          this.animateElement(element);
        }
      } else if (element.options.mirror && element.animated) {
        this.removeAnimation(element);
      }
    });
  }

  /**
   * Check if AOS should be disabled
   */
  shouldDisable() {
    const { disable } = this.options;

    if (typeof disable === 'boolean') {
      return disable;
    }

    if (typeof disable === 'string') {
      return window.innerWidth <= parseInt(disable);
    }

    if (typeof disable === 'function') {
      return disable();
    }

    // Respect user preference for reduced motion
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true;
    }

    return false;
  }

  /**
   * Bind scroll and resize events
   */
  bindEvents() {
    window.addEventListener('scroll', this.throttledScrollHandler);
    window.addEventListener('resize', this.debounce(this.resizeListener, this.options.debounceDelay));

    // Handle page transitions (for Shopify themes)
    if (window.Shopify && window.Shopify.theme) {
      document.addEventListener('shopify:section:load', () => {
        setTimeout(() => this.refresh(), 100);
      });
    }
  }

  /**
   * Unbind events
   */
  unbindEvents() {
    window.removeEventListener('scroll', this.throttledScrollHandler);
    window.removeEventListener('resize', this.resizeListener);
  }

  /**
   * Throttle function
   */
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * Debounce function
   */
  debounce(func, wait, immediate) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      const later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }

  /**
   * Destroy AOS instance
   */
  destroy() {
    this.unbindEvents();

    // Remove classes from elements
    this.elements.forEach(element => {
      element.node.classList.remove(this.options.animatedClassName);
    });

    // Remove body class
    if (this.options.useClassOnHtml) {
      document.documentElement.classList.remove(this.options.initClassName);
    } else {
      document.body.classList.remove(this.options.initClassName);
    }

    this.initialized = false;
    this.elements = [];
  }
}

// Create global AOS instance
window.AOS = new AOS();

// Auto-initialize on DOM ready if data-aos-init attribute is present
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-aos-init]') || document.body.hasAttribute('data-aos-init')) {
    window.AOS.init();
  }
});

// Shopify theme compatibility
if (window.Shopify && window.Shopify.theme) {
  // Handle section reloads in theme editor
  document.addEventListener('shopify:section:load', (event) => {
    setTimeout(() => {
      if (window.AOS.initialized) {
        window.AOS.refresh();
      }
    }, 100);
  });

  // Handle section unloads
  document.addEventListener('shopify:section:unload', (event) => {
    if (window.AOS.initialized) {
      window.AOS.refresh();
    }
  });
}

export default AOS;